import React, { useState, useEffect } from 'react';
import { fetchSetting, updateSetting } from '../services/settingsService';

/**
 * @param {{ variant?: 'page' | 'embedded' }} [props] — `embedded`: Ayarlar hub içinde, dış sayfa başlığı yok
 */
const KmSettingsPage = ({ variant = 'page' }) => {
  const embedded = variant === 'embedded';
  const [kmDegeri, setKmDegeri] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    loadKmDegeri();
  }, []);

  const loadKmDegeri = async () => {
    try {
      setLoading(true);
      const response = await fetchSetting('KM_DEGERI');
      
      if (response?.basarili && response?.veri?.setting) {
        setKmDegeri(response.veri.setting.deger || '0');
      } else {
        setKmDegeri('0');
      }
    } catch (error) {
      console.error('KM değeri yüklenemedi:', error);
      setKmDegeri('0');
      setMessage({ type: 'info', text: 'KM değeri henüz ayarlanmamış. Lütfen bir değer girin.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    const deger = parseFloat(kmDegeri);
    if (isNaN(deger) || deger < 0) {
      setMessage({ type: 'error', text: 'Lütfen geçerli bir sayı girin (0 veya daha büyük)' });
      return;
    }

    try {
      setSaving(true);
      setMessage({ type: '', text: '' });

      const response = await updateSetting('KM_DEGERI', {
        deger: deger,
        aciklama: 'Kilometre başına ödenecek tutar (TL/km) - Hakediş hesaplamalarında kullanılır',
        tip: 'number',
      });

      if (response?.basarili) {
        setMessage({ type: 'success', text: 'KM değeri başarıyla güncellendi!' });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      } else {
        setMessage({ type: 'error', text: response?.mesaj || 'Güncelleme başarısız' });
      }
    } catch (error) {
      console.error('KM değeri güncellenemedi:', error);
      setMessage({ type: 'error', text: 'Bir hata oluştu. Lütfen tekrar deneyin.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    const loader = (
      <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
        <p style={{ color: '#6b7280' }}>Yükleniyor...</p>
      </div>
    );
    if (embedded) return loader;
    return (
      <div className="page-container">
        <div className="content-header">
          <div>
            <h1>KM Ayarları</h1>
          </div>
        </div>
        {loader}
      </div>
    );
  }

  const card = (
      <div className="card" style={{ maxWidth: '700px', margin: embedded ? 0 : '0 auto' }}>
        <form onSubmit={handleSave}>
          <div style={{ marginBottom: '24px' }}>
            <label 
              htmlFor="kmDegeri" 
              style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: 500, 
                color: '#374151',
                fontSize: '14px'
              }}
            >
              <i className="fas fa-road" style={{ marginRight: '8px', color: '#3b82f6' }}></i>
              Kilometre Başına Tutar (TL/km)
            </label>
            <input
              type="number"
              id="kmDegeri"
              value={kmDegeri}
              onChange={(e) => setKmDegeri(e.target.value)}
              placeholder="Örn: 5.50"
              step="0.01"
              min="0"
              required
              disabled={saving}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '16px',
                transition: 'border-color 0.2s',
              }}
              onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            />
            <small style={{ 
              display: 'block', 
              marginTop: '6px', 
              fontSize: '13px', 
              color: '#6b7280' 
            }}>
              Örnek: 5.50 TL girdiğinizde, 10 km gidiş-dönüş mesafe için 55 TL yol bedeli hesaplanır.
            </small>
          </div>

          {message.text && (
            <div style={{
              padding: '12px 16px',
              marginBottom: '20px',
              borderRadius: '6px',
              backgroundColor: message.type === 'success' ? '#d1fae5' : message.type === 'error' ? '#fee2e2' : '#dbeafe',
              color: message.type === 'success' ? '#065f46' : message.type === 'error' ? '#991b1b' : '#1e40af',
              border: `1px solid ${message.type === 'success' ? '#10b981' : message.type === 'error' ? '#ef4444' : '#3b82f6'}`,
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}>
              <i className={`fas fa-${message.type === 'success' ? 'check-circle' : message.type === 'error' ? 'exclamation-circle' : 'info-circle'}`}></i>
              <span>{message.text}</span>
            </div>
          )}

          <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
            <button
              type="submit"
              disabled={saving}
              style={{
                padding: '12px 24px',
                backgroundColor: saving ? '#9ca3af' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: saving ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => {
                if (!saving) e.target.style.backgroundColor = '#2563eb';
              }}
              onMouseLeave={(e) => {
                if (!saving) e.target.style.backgroundColor = '#3b82f6';
              }}
            >
              {saving ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Kaydediliyor...
                </>
              ) : (
                <>
                  <i className="fas fa-save"></i>
                  Kaydet
                </>
              )}
            </button>
          </div>
        </form>

        <div style={{
          marginTop: '32px',
          padding: '20px',
          backgroundColor: '#f9fafb',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
        }}>
          <h3 style={{
            fontSize: '15px',
            fontWeight: 600,
            color: '#111827',
            marginBottom: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <i className="fas fa-info-circle" style={{ color: '#3b82f6' }}></i>
            Bilgilendirme
          </h3>
          <ul style={{
            margin: 0,
            paddingLeft: '24px',
            fontSize: '14px',
            color: '#4b5563',
            lineHeight: '1.8',
          }}>
            <li style={{ marginBottom: '8px' }}>
              Bu ayar tüm hakediş hesaplamalarında kullanılır.
            </li>
            <li style={{ marginBottom: '8px' }}>
              Randevu tamamlandığında, gidiş-dönüş mesafe × KM değeri ile yol bedeli otomatik hesaplanır.
            </li>
            <li>
              Değer değiştirildiğinde sadece yeni randevular etkilenir, eski hakedişler değişmez.
            </li>
          </ul>
        </div>
      </div>
  );

  if (embedded) return card;

  return (
    <div className="page-container">
      <div className="content-header">
        <div>
          <h1>KM Ayarları</h1>
        </div>
      </div>
      {card}
    </div>
  );
};

export default KmSettingsPage;
